import * as React from 'react';
import { useLatestRef } from '@epihub/web/src/hooks/useLatestRef';

export function useTarget<Props extends { style?: any }>(
  Component: React.FC<Props>,
  id: string,
  styles?: (rect: DOMRect) => Partial<any>,
) {
  const [position, setPosition] = React.useState<DOMRect | null>(null);

  const Target = React.useMemo(() => (props: Props) => {
    if (!position) {
      return null;
    }

    const compOffset = styles ?? ((rect: DOMRect) => ({
      top: rect.y,
      left: rect.x,
      width: rect.width,
    }));
    const offset = compOffset(position);
    return (
      <Component
        {...props}
        style={{
          ...props?.style,
          position: 'absolute',
          ...offset,
        }}
      />
    );
  }, [position, Component, styles]);

  const measureRef = useLatestRef(() => {
    setPosition(document.getElementById(id)?.getBoundingClientRect() ?? null);
  });

  const destroyRef = useLatestRef(() => {
    setPosition(null);
  });

  return {
    Target,
    measureRef,
    destroyRef,
  };
}
